import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useAuth } from "../../context/AuthContext";

// There was previously no way to see, deactivate, or change the role of an
// existing account once created - Create Admin only ever wrote new
// accounts, never listed them. Open to plain admins too, but they can't
// touch a super admin's account (see canManageSuperAdmin below) - the
// server enforces the same restriction, this just keeps the button from
// being offered and immediately failing.
const Staff = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const token = localStorage.getItem("authToken");

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.users);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Couldn't load staff accounts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchUser = async (id, body) => {
    setError(null);
    setBusyId(id);
    try {
      await axios.patch(`${API_BASE_URL}/auth/users/${id}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchUsers();
    } catch (err) {
      console.error("Error updating user:", err);
      setError(err.response?.data || "Failed to update that account.");
    } finally {
      setBusyId(null);
    }
  };

  const canManageSuperAdmin = user?.role === "super_admin";

  const toggleActive = (target) => patchUser(target._id, { active: !target.active });
  const toggleRole = (target) =>
    patchUser(target._id, { role: target.role === "admin" ? "user" : "admin" });

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sub">Loading staff accounts…</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4 text-ink">Staff Accounts</h2>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-4 max-w-2xl">
          {error}
        </p>
      )}

      <div className="bg-card border border-line rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-paper text-left text-xs uppercase tracking-wide text-muted border-b border-line">
            <tr>
              <th className="p-3">Name</th>
              <th className="p-3">Email</th>
              <th className="p-3">Role</th>
              <th className="p-3">Status</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {users.map((account) => {
              const isSelf = account._id === user?.id || account.email === user?.email;
              const busy = busyId === account._id;
              return (
                <tr key={account._id} className="hover:bg-paper/60 transition">
                  <td className="p-3 text-ink">
                    {account.firstName} {account.lastName}
                    {isSelf && <span className="text-xs text-muted ml-1">(you)</span>}
                  </td>
                  <td className="p-3 text-sub">{account.email}</td>
                  <td className="p-3 text-ink capitalize">{account.role.replace("_", " ")}</td>
                  <td className="p-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        account.active === false
                          ? "bg-red-50 text-red-700"
                          : "bg-green-50 text-green-700"
                      }`}
                    >
                      {account.active === false ? "Deactivated" : "Active"}
                    </span>
                  </td>
                  <td className="p-3 space-x-3">
                    {account.role !== "super_admin" && (
                      <button
                        onClick={() => toggleRole(account)}
                        disabled={busy}
                        className="text-primary text-xs font-semibold disabled:opacity-50"
                      >
                        Make {account.role === "admin" ? "User" : "Admin"}
                      </button>
                    )}
                    {!isSelf && (account.role !== "super_admin" || canManageSuperAdmin) && (
                      <button
                        onClick={() => toggleActive(account)}
                        disabled={busy}
                        className="text-xs font-semibold text-red-600 disabled:opacity-50"
                      >
                        {account.active === false ? "Reactivate" : "Deactivate"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Staff;
